import {
  Box,
  capitalize,
  Card,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { UserAvatar } from "../../common/components/UserAvatar";
import { SPACING } from "../../common/constants/spacing";
import { getPlatformLogo } from "../../common/utils/platformUtils";

export const SourceItem = ({ source }) => {
  const { t } = useTranslation("sources");
  const navigate = useNavigate();

  const handleViewClick = () => {
    navigate(`/sources/${source.id}`);
  };

  const formatLastFetchedTime = (timestamp) => {
    if (!timestamp) return t("sourceItem.never");
    const date = new Date(timestamp);
    const now = new Date();

    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    const formattedTime = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    return isToday
      ? `Today, ${formattedTime}`
      : `${date.toLocaleDateString()}, ${formattedTime}`;
  };

  return (
    <Grid sx={{ width: "100%" }}>
      <Card sx={{ p: 3 }} variant="outlined">
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid container spacing={2} alignItems="center">
            <Grid
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pr: 1,
                position: "relative",
              }}
            >
              {/* Avatar/Profile Photo with initials fallback */}
              <UserAvatar
                name={source.account_name}
                image={source.avatar}
                size={56}
              />

              {/* Platform Icon Badge */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: -4,
                  right: 4,
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor: "background.paper",
                  border: "2px solid",
                  borderColor: "background.paper",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: 1,
                }}
              >
                <img
                  src={getPlatformLogo(source.platform)}
                  alt={`${source.platform} Logo`}
                  style={{
                    width: "16px",
                    height: "16px",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Grid>
            <Grid>
              <Typography
                variant="h3"
                sx={{ fontWeight: "bold", mb: SPACING.xs }}
              >
                {source.account_name}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="bodySm" sx={{ color: "text.secondary" }}>
                  <Typography
                    sx={{ fontWeight: 500, mr: SPACING.xxs }}
                    variant="bodySm"
                  >
                    {t("sourceItem.lastFetch")}
                  </Typography>
                  {formatLastFetchedTime(source.last_fetched_at)}
                </Typography>
                <Typography variant="bodySm" sx={{ color: "text.secondary" }}>
                  <Typography
                    sx={{ fontWeight: 500, mr: SPACING.xxs }}
                    variant="bodySm"
                  >
                    {t("sourceItem.platform")}
                  </Typography>
                  {capitalize(source.platform)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Grid
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <IconButton color="primary" onClick={handleViewClick}>
              <ArrowRight />
            </IconButton>
          </Grid>
        </Grid>
      </Card>
    </Grid>
  );
};
